import { useEffect, useState } from "react";
import API from "../../../Network/API";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { USER_LOGGED_IN } from "../../../Reducer/authSlice";

export const PLAN_HIGHLIGHTS = [
    { icon: "fa-light fa-video", text: "subscription.highlight_unlimited_live_classes" },
    { icon: "fa-light fa-users", text: "subscription.highlight_unlimited_live_sessions" },
    { icon: "fa-light fa-hand-holding-dollar", text: "subscription.highlight_zero_commission" },
    { icon: "fa-light fa-clapperboard-play", text: "subscription.highlight_recorded_courses" },
    { icon: "fa-light fa-address-card", text: "subscription.highlight_featured_listing" },
];

// Keep the stored login in step with the server's view of the plan.
export const storeUser = (dispatch, user) => {
    if (!user) return;
    const auth = { user, accessToken: localStorage.getItem("xller_token") };
    localStorage.setItem("xller_auth", JSON.stringify(auth));
    dispatch(USER_LOGGED_IN(auth));
};

export const formatPlanDate = (iso, lng) =>
    iso ? new Date(iso).toLocaleDateString(lng || "en", { day: "numeric", month: "short", year: "numeric" }) : "";

// The date a trial started today would end.
export const trialEndDate = (days) => new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();

export default function Subscription() {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const [data, setData] = useState(null);
    const [trialDays, setTrialDays] = useState(0);
    const [isLoading, setLoading] = useState(false);
    const [plan, setPlan] = useState(null);
    const [busy, setBusy] = useState(false);
    const { user } = useSelector((state) => state.authReducer);
    const location = useLocation();
    const prefix = location.pathname.startsWith("/app") ? "/app" : "";
    const lng = i18n.resolvedLanguage || i18n.language;

    useEffect(() => {
        setLoading(true)
        API.get(`/v1/oauth/teacher/packages`).then((response) => {
            setData(response.data.data.packages)
            setTrialDays(Number(response.data.data.trial_days) || 0)
            setLoading(false)
        }).catch((error) => {
            setLoading(false)
        });

    }, []);

    // Current plan straight from Stripe; also drops the subscribed flag once Stripe has ended it.
    useEffect(() => {
        if (!user?.is_subscribed) return;
        API.get(`/v1/oauth/teacher/subscription`).then((response) => {
            setPlan(response.data.data.subscription)
            storeUser(dispatch, response.data.data.user)
        }).catch(() => { });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.is_subscribed]);

    const changePlan = (action) => {
        if (action === "cancel" && !window.confirm(t("subscription.cancel_confirm", { date: formatPlanDate(plan?.current_period_end, lng) }))) {
            return;
        }
        setBusy(true)
        API.post(`/v1/oauth/teacher/subscription/${action}`).then((response) => {
            setPlan(response.data.data.subscription)
            toast.success(response.data.message)
            setBusy(false)
        }).catch((err) => {
            toast.error(err?.response?.data?.message || t("subscription.update_failed"))
            setBusy(false)
        });
    };

    const planStatus = () => {
        if (!plan) return t("subscription.subscribed_message");
        const end = formatPlanDate(plan.current_period_end, lng);
        if (plan.cancel_at_period_end) return t("subscription.status_cancelling", { date: end });
        if (plan.on_trial) return t("subscription.status_trial", { date: formatPlanDate(plan.trial_ends_at, lng), price: plan.price });
        return end ? t("subscription.status_active", { date: end, price: plan.price }) : t("subscription.subscribed_message");
    };

    return (
        <>
            <div className="sub-hero text-center">
                <span className="sub-hero-badge"><i className="fa-light fa-crown me-2" />{t("subscription.room_rent_monthly_plan")}</span>
                <h2 className="sub-hero-title">{t("subscription.teach_without_limits")}</h2>
                <p className="sub-hero-dec">
                    {t("subscription.hero_description")}
                </p>
            </div>

            <div className="row justify-content-center">
                    {!isLoading && data?.length > 0 && data?.map((pack, j) => (
                        <div className="col-xl-5 col-lg-6 col-md-8" key={j}>
                            <div className="pricing-card">
                                <div className="pricing-card-head">
                                    {!user?.is_subscribed && trialDays > 0 &&
                                        <span className="pricing-trial-badge"><i className="fa-light fa-gift me-2" />{t("subscription.trial_badge", { days: trialDays })}</span>
                                    }
                                    <h4 className="pricing-card-name">{pack.title}</h4>
                                    <div className="pricing-card-price">
                                        <span className="pricing-amount">${pack.price}</span>
                                        <span className="pricing-period">{t("subscription.per_month")}</span>
                                    </div>
                                    {!user?.is_subscribed && trialDays > 0 &&
                                        <p className="pricing-card-note">{t("subscription.trial_then", { days: trialDays, price: pack.price })}</p>
                                    }
                                    {pack.details ? <p className="pricing-card-note">{pack.details}</p> : null}
                                </div>

                                <ul className="list-unstyled pricing-features">
                                    {PLAN_HIGHLIGHTS.map((f, i) => (
                                        <li key={i}>
                                            <span className="pricing-feature-icon"><i className={f.icon} /></span>
                                            <span>{t(f.text)}</span>
                                        </li>
                                    ))}
                                </ul>

                                {user?.is_subscribed ?
                                    <>
                                        <div className={`subscribed-panel ${plan?.cancel_at_period_end ? 'is-ending' : ''}`}>
                                            <i className={`fa-solid ${plan?.cancel_at_period_end ? 'fa-clock' : 'fa-circle-check'} me-2`} />
                                            {planStatus()}
                                        </div>
                                        {plan && (plan.cancel_at_period_end ?
                                            <button type="button" disabled={busy} onClick={() => changePlan("resume")} className="w-100 btn subscribe-btn mt-3">
                                                {busy && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />}
                                                {t("subscription.resume_cta")}
                                            </button>
                                            :
                                            <button type="button" disabled={busy} onClick={() => changePlan("cancel")} className="w-100 btn sub-cancel-btn mt-3">
                                                {busy && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />}
                                                {plan.on_trial ? t("subscription.cancel_trial_cta") : t("subscription.cancel_cta")}
                                            </button>
                                        )}
                                    </>
                                    :
                                    <Link to={`${prefix}/dashboard/subscription/checkout?package=${pack.id}`} className="w-100 btn subscribe-btn mt-2">
                                        {trialDays > 0 ?
                                            <><i className="fa-light fa-gift me-2" />{t("subscription.start_trial_cta", { days: trialDays })}</>
                                            :
                                            <><i className="fa-light fa-lock me-2" />{t("subscription.subscribe_cta", { price: pack.price })}</>
                                        }
                                    </Link>
                                }
                            </div>
                        </div>
                    ))}

            </div>

            {!user?.is_subscribed &&
                <p className="text-center sub-footnote">
                    {trialDays > 0 ?
                        t("subscription.footnote_trial", { days: trialDays, date: formatPlanDate(trialEndDate(trialDays), lng) })
                        : t("subscription.footnote")}
                </p>
            }
        </>
    )
}
